"use client";

import { useEffect, useMemo, useRef } from "react";
import { useAnalyzeFileContext } from "../context/AnalyzeFileContext";
import { useOptionalFileDetailModal } from "../../FileDetailModal/context/FileDetailModalContext";
import { analyzeIdsSignature } from "../analyzeImageOverlayUtils";

/** Pushes analyze panel state to FileDetailModal for the on-image overlay. */
export const AnalyzeOverlaySync = () => {
  const fileDetail = useOptionalFileDetailModal();
  const setAnalyzeImageOverlay = fileDetail?.setAnalyzeImageOverlay;

  const { personas, feedFilter, typingPersonas } = useAnalyzeFileContext();

  const generatingPersonaIds = useMemo(
    () => Array.from(typingPersonas).sort(),
    [typingPersonas],
  );

  const generatingKey = useMemo(() => analyzeIdsSignature(generatingPersonaIds), [generatingPersonaIds]);
  const isGenerating = generatingPersonaIds.length > 0;
  const scanPersonaId = generatingPersonaIds[0] ?? feedFilter;

  const scanPersonaName = useMemo(() => {
    if (!scanPersonaId) return null;
    return personas.find((p) => p.id === scanPersonaId)?.name ?? null;
  }, [scanPersonaId, personas]);

  const overlaySyncKey = useMemo(() => {
    const nextScanPersonaId = isGenerating ? scanPersonaId : feedFilter;
    const nextScanPersonaName = isGenerating ? scanPersonaName : null;
    return [
      feedFilter ?? "",
      generatingKey,
      nextScanPersonaId ?? "",
      nextScanPersonaName ?? "",
      isGenerating ? "1" : "0",
    ].join("::");
  }, [feedFilter, generatingKey, scanPersonaId, scanPersonaName, isGenerating]);

  const lastOverlaySyncKeyRef = useRef<string | null>(null);
  const isAnalyzeOpen = fileDetail?.isAnalyzeDialogOpen ?? false;

  useEffect(() => {
    if (!isAnalyzeOpen) {
      lastOverlaySyncKeyRef.current = null;
    }
  }, [isAnalyzeOpen]);

  useEffect(() => {
    if (!setAnalyzeImageOverlay) return;
    if (lastOverlaySyncKeyRef.current === overlaySyncKey) return;
    lastOverlaySyncKeyRef.current = overlaySyncKey;

    const nextScanPersonaId = isGenerating ? scanPersonaId : feedFilter;
    const nextScanPersonaName = isGenerating ? scanPersonaName : null;

    setAnalyzeImageOverlay((prev) => {
      if (
        prev.feedFilter === feedFilter &&
        analyzeIdsSignature(prev.generatingPersonaIds) === generatingKey &&
        prev.scanPersonaId === nextScanPersonaId &&
        prev.scanPersonaName === nextScanPersonaName
      ) {
        return prev;
      }

      return {
        ...prev,
        feedFilter,
        generatingPersonaIds,
        scanPersonaId: nextScanPersonaId,
        scanPersonaName: nextScanPersonaName,
      };
    });
  }, [
    setAnalyzeImageOverlay,
    overlaySyncKey,
    feedFilter,
    generatingPersonaIds,
    generatingKey,
    scanPersonaId,
    scanPersonaName,
    isGenerating,
  ]);

  return null;
};
